import { getLodop } from '@/utils/lodopFuncs'

// 单例实例
let lodopInstance = null

/**
 * 获取或初始化 LODOP 实例
 * 如果未初始化则尝试初始化，如果初始化失败返回 null
 */
const getLodopInstance = () => {
  if (!lodopInstance) {
    try {
      lodopInstance = getLodop()
      // 某些环境下 getLodop 可能返回 null 或未定义，需二次确认
      if (!lodopInstance || typeof lodopInstance.PRINT_INIT !== 'function') {
        console.warn('LODOP 控件未就绪或加载失败')
        return null
      }
    } catch (error) {
      console.error('LODOP 初始化异常:', error)
      return null
    }
  }
  return lodopInstance
}

/**
 * 获取打印机列表
 * @returns {Array<{label: string, value: number}>}
 */
export const getPrinters = () => {
  const lodop = getLodopInstance()
  if (!lodop) return []

  const printerCount = lodop.GET_PRINTER_COUNT()
  const printerOptions = []

  for (let i = 0; i < printerCount; i++) {
    const printerName = lodop.GET_PRINTER_NAME(i)
    if (printerName) {
      printerOptions.push({
        label: printerName,
        value: i,
      })
    }
  }
  return printerOptions
}

/**
 * 初始化打印任务
 * @param {string} taskName - 任务名称
 * @param {number|string} printerIndex - 打印机索引或名称
 * @param {string} pageWidth - 纸张宽度 (如 '70mm')
 * @param {string} pageHeight - 纸张高度 (如 '40mm')
 * @returns {object} 返回代理对象以支持链式调用
 */
// export const initPrint = (taskName = '打印任务', printerIndex = 0, pageWidth = '70mm', pageHeight = '40mm') => {
//   const lodop = getLodopInstance()
//   if (!lodop) return createChainProxy(null)

//   lodop.PRINT_INIT(taskName)
//   lodop.SET_PRINTER_INDEX(printerIndex)
//   // 第三个参数是方向，0表示纵向，这里保持原逻辑
//   lodop.SET_PRINT_PAGESIZE(0, pageWidth, pageHeight, '')

//   return createChainProxy(lodop)
// }
export const initPrint = (taskName = '打印任务', printerName = '', callback = null, pageWidth = '210mm', pageHeight = '297mm', autoScale = true) => {
  const lodop = getLodopInstance()
  if (!lodop) return createChainProxy(null)

  // 初始化
  lodop.PRINT_INIT(taskName)

  lodop.SET_PRINT_STYLE('FontName', 'Arial')
  //lodop.SET_PRINT_STYLE('FontSize', 10)
  //lodop.SET_PRINT_STYLE('FontName', '微软雅黑')
  // 设置打印模式：高度自适应
  //lodop.SET_PRINT_MODE('PRINT_PAGE_PERCENT', 'Auto-Height')
  //纸张设置：自定义模式
  lodop.SET_PRINT_PAGESIZE(0, pageWidth, pageHeight, '')

  //内容自适应，会根据纸张大小缩放内容
  if (autoScale) {
    lodop.SET_PRINT_MODE('PRINT_PAGE_PERCENT', 'Full-Page')
    lodop.SET_PRINT_MODE('FULL_WIDTH_FOR_OVERFLOW', true)
  }
  if (!printerName || printerName.trim() === '') {
    lodop.SET_PRINTER_INDEX(0)
  } else {
    lodop.SET_PRINTER_INDEXA(printerName)
  }
  if (typeof callback === 'function') {
    // 不要加括号 ()，否则会变成立即执行！
    // LODOP 的 On_Return 会在打印窗口关闭时触发，传入一个参数 (Action):
    // 1=打印成功，2=打印取消/关闭窗口
    lodop.On_Return = function (Action) {
      callback(Action)
    }
  } else {
    lodop.On_Return = null
  }
  return createChainProxy(lodop)
}

/**
 * 创建链式调用代理
 * 允许像 lodop.setStyle(...).addText(...) 这样调用
 */
const createChainProxy = lodop => {
  if (!lodop) {
    // 如果 lodop 为空，返回一个空操作代理，防止报错
    return new Proxy(
      {},
      {
        get: () => () => createChainProxy(null),
      }
    )
  }

  return {
    setStyle: (styleName, styleValue) => {
      lodop.SET_PRINT_STYLE(styleName, styleValue)
      return createChainProxy(lodop)
    },
    addText: (top, left, width, height, content) => {
      lodop.ADD_PRINT_TEXT(top, left, width, height, content)
      return createChainProxy(lodop)
    },
    addHtml: (top, left, width, height, content) => {
      lodop.ADD_PRINT_HTML(top, left, width, height, content)
      return createChainProxy(lodop)
    },
    addBarcode: (top, left, width, height, codeValue, codeType = '128Auto') => {
      lodop.ADD_PRINT_BARCODE(top, left, width, height, codeType, codeValue)
      return createChainProxy(lodop)
    },
    addLine: (top, left, right, bottom, style, penWidth = 1) => {
      lodop.ADD_PRINT_LINE(top, left, right, bottom, style, penWidth)
      return createChainProxy(lodop)
    },
    addRect: (top, left, width, height, style, penWidth = 1) => {
      lodop.ADD_PRINT_RECT(top, left, width, height, style, penWidth)
      return createChainProxy(lodop)
    },
    addImage: (top, left, width, height, imgData) => {
      lodop.ADD_PRINT_IMAGE(top, left, width, height, imgData)
      return createChainProxy(lodop)
    },
    addEllipse: (top, left, width, height, style, penWidth) => {
      lodop.ADD_PRINT_ELLIPSE(top, left, width, height, style, penWidth)
    },
    newPage: () => {
      lodop.NEWPAGE()
      return createChainProxy(lodop)
    },
    preview: () => {
      lodop.PREVIEW()
      return createChainProxy(lodop)
    },
    print: () => {
      const result = lodop.PRINT()
      // 打印完成后重置单例？通常不需要，除非需要强制重新检测打印机
      // 如果需要每次打印后重置，可在此处设置 lodopInstance = null
      return result
    },
    // 暴露原生实例以防需要调用其他未封装方法
    native: lodop,
  }
}

// 为了方便直接调用独立函数（兼容旧写法），同时也导出链式对象
export default {
  getPrinters,
  initPrint,
  // 以下方法主要通过 initPrint 返回的链式对象调用，但也提供独立调用方式（不推荐混用）
  // 如果必须独立调用，它们内部会再次获取实例，但无法维持上下文状态（如分页状态）
  // 因此建议主要使用 initPrint 开始的链式调用
}
